package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.History
import androidx.compose.material.icons.outlined.Badge
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.i18n.label
import org.gymly.i18n.membershipsStrings
import org.gymly.i18n.strings
import org.gymly.model.AdminClient
import org.gymly.model.AdminMembershipAccessMode
import org.gymly.model.AdminMembershipItem
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.AdminMembershipStatus
import org.gymly.model.AdminMembershipUpsert
import org.gymly.model.AdminStaffRole
import org.gymly.model.AdminTrainer
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.AdminDateField
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn

private enum class MembershipsDateFilter(val days: Int?) {
    Days7(7),
    Days30(30),
    All(null),
}

@Composable
private fun MembershipsDateFilter.filterLabel(): String {
    val tm = membershipsStrings()
    return when (this) {
        MembershipsDateFilter.Days7 -> tm.rangeDays7
        MembershipsDateFilter.Days30 -> tm.rangeDays30
        MembershipsDateFilter.All -> tm.rangeAll
    }
}

private sealed class MembershipsDialog {
    data object Create : MembershipsDialog()
    data class Edit(val item: AdminMembershipItem) : MembershipsDialog()
    data class Detail(val item: AdminMembershipItem) : MembershipsDialog()
    data class Delete(val item: AdminMembershipItem) : MembershipsDialog()
    data class History(val item: AdminMembershipItem) : MembershipsDialog()
}

@Composable
fun AdminMembershipsScreen(canMembershipOverride: Boolean = false) {
    val t = strings()
    val tm = membershipsStrings()
    val scope = rememberCoroutineScope()
    var items by remember { mutableStateOf<List<AdminMembershipItem>>(emptyList()) }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var plans by remember { mutableStateOf<List<AdminMembershipPlanItem>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var trainers by remember { mutableStateOf<List<AdminTrainer>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(MembershipsDateFilter.All) }
    var dialog by remember { mutableStateOf<MembershipsDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    val pendingApprovals = LocalPendingApprovalNotifier.current

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                items = AdminRepo.api.listMemberships()
                clients = AdminRepo.api.listClients()
                plans = AdminRepo.api.listMembershipPlans()
                companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                    AdminRepo.api.listBranches().firstOrNull()?.id
                }
                trainers = run {
                    val fromApi = runCatching { AdminRepo.api.listTrainers() }.getOrDefault(emptyList())
                    if (fromApi.isNotEmpty()) {
                        fromApi
                    } else {
                        AdminRepo.api.listWorkers()
                            .filter { it.role == AdminStaffRole.TRAINER || it.role == AdminStaffRole.ADMIN }
                            .map { AdminTrainer(it.id, it.displayName, it.email, it.role.name) }
                    }
                }
            } catch (e: Exception) {
                error = e.userMessage(tm.loadFailed)
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(items, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        items.filter { m ->
            val matchesQuery = q.isEmpty() ||
                m.clientName.lowercase().contains(q) ||
                m.planName.lowercase().contains(q) ||
                (m.trainerName?.lowercase()?.contains(q) == true) ||
                m.status.labelEs().lowercase().contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = m.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "client",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "plan" -> { m -> m.planName.lowercase() }
                "status" -> { m -> m.status.name }
                "starts" -> { m -> m.startsAt }
                "trainer" -> { m -> m.trainerName?.lowercase() }
                else -> { m -> m.clientName.lowercase() }
            }
        }
    }

    val total = filtered.size
    val active = filtered.count { it.status == AdminMembershipStatus.ACTIVE }
    val expired = filtered.count { it.status == AdminMembershipStatus.EXPIRED }
    val cancelled = filtered.count { it.status == AdminMembershipStatus.CANCELLED }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        Column(Modifier.fillMaxSize()) {
            MembershipsHeader(dateFilter) { dateFilter = it }
            Spacer(Modifier.height(12.dp))
            MembershipAccessInfoBanner()
            Spacer(Modifier.height(12.dp))
            MembershipsKpiRow(total, active, expired, cancelled, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            MembershipsDirectoryCard(
                memberships = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                clients = clients,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                canMembershipOverride = canMembershipOverride,
                onCreate = { dialog = MembershipsDialog.Create },
                onView = { dialog = MembershipsDialog.Detail(it) },
                onEdit = { dialog = MembershipsDialog.Edit(it) },
                onDelete = { dialog = MembershipsDialog.Delete(it) },
                onHistory = { dialog = MembershipsDialog.History(it) },
            )
        }
    }

    when (val d = dialog) {
        is MembershipsDialog.Detail -> MembershipDetailDialog(
            item = d.item,
            canMembershipOverride = canMembershipOverride,
            onDismiss = { dialog = null },
            onBlocked = { reload() },
            onReleased = { reload() },
        )
        is MembershipsDialog.Create -> MembershipFormDialog(
            title = tm.manualActivationTitle,
            initial = null,
            clients = clients,
            plans = plans,
            companyId = companyId,
            trainers = trainers,
            saving = saving,
            manualOnly = true,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createMembership(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage(tm.assignFailed)
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is MembershipsDialog.Edit -> MembershipFormDialog(
            title = tm.editTitle,
            initial = d.item,
            clients = clients,
            plans = plans,
            companyId = companyId,
            trainers = trainers,
            saving = saving,
            manualOnly = canMembershipOverride,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateMembership(d.item.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage(tm.saveFailed)
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is MembershipsDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text(tm.deleteTitle) },
            text = {
                Text(tm.deleteConfirm(d.item.clientName))
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteMembership(d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) {
                                    dialog = null
                                    error = null
                                } else {
                                    error = e.userMessage(tm.deleteFailed)
                                }
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else t.common.delete)
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text(t.common.cancel, color = AdminColors.TextMuted)
                }
            },
        )
        is MembershipsDialog.History -> MembershipAccessHistoryDialog(
            item = d.item,
            clientMemberships = items
                .filter { it.clientId == d.item.clientId }
                .sortedByDescending { it.startsAt },
            onDismiss = { dialog = null },
        )
        null -> Unit
    }
}

@Composable
private fun MembershipsHeader(
    dateFilter: MembershipsDateFilter,
    onFilterChange: (MembershipsDateFilter) -> Unit,
) {
    val tm = membershipsStrings()
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    Icons.Outlined.Badge,
                    contentDescription = null,
                    tint = Color(0xFF2E7D32),
                    modifier = Modifier.size(28.dp),
                )
                Spacer(Modifier.width(10.dp))
                Text(tm.title, fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            }
            Text(
                tm.subtitle,
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 6.dp),
            )
            Text(
                tm.enrollHint,
                color = Color(0xFF2E7D32),
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                Text(dateFilter.filterLabel(), color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                MembershipsDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.filterLabel()) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun MembershipsKpiRow(
    total: Int,
    active: Int,
    expired: Int,
    cancelled: Int,
    compact: Boolean,
) {
    val tm = membershipsStrings()
    val kpiItems = listOf(
        Triple(tm.kpiMembers, total.toString(), AdminColors.Primary),
        Triple(tm.kpiActiveAccess, active.toString(), Color(0xFF2E7D32)),
        Triple(tm.kpiExpired, expired.toString(), Color(0xFF757575)),
        Triple(tm.kpiCancelled, cancelled.toString(), Color(0xFFC62828)),
    )
    if (compact) {
        // Celular: franja de indicadores de una línea, para dejar el espacio a la lista.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            kpiItems.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            kpiItems.forEach { (label, value, accent) ->
                MembershipKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun MembershipKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun MembershipAccessInfoBanner() {
    val tm = membershipsStrings()
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color(0xFFE8F5E9))
            .border(1.dp, Color(0xFF2E7D32).copy(alpha = 0.25f), RoundedCornerShape(12.dp))
            .padding(14.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Icon(Icons.Outlined.Info, null, tint = Color(0xFF2E7D32), modifier = Modifier.size(20.dp))
        Spacer(Modifier.width(10.dp))
        Column {
            Text(
                tm.accessBannerTitle,
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                color = Color(0xFF1B5E20),
            )
            Text(
                tm.accessBannerBody,
                color = Color(0xFF2E7D32),
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 2.dp),
            )
        }
    }
}

@Composable
private fun ColumnScope.MembershipsDirectoryCard(
    memberships: List<AdminMembershipItem>,
    totalCount: Int,
    listPaging: AdminListPaging,
    clients: List<AdminClient>,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    canMembershipOverride: Boolean,
    onCreate: () -> Unit,
    onView: (AdminMembershipItem) -> Unit,
    onEdit: (AdminMembershipItem) -> Unit,
    onDelete: (AdminMembershipItem) -> Unit,
    onHistory: (AdminMembershipItem) -> Unit,
) {
    val tm = membershipsStrings()
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(tm.directoryTitle, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                MembershipSearchField(query, onQueryChange)
                if (canMembershipOverride) {
                    if (canWriteModule(AdminModuleAccess.MEMBERSHIPS)) Button(
                        onClick = onCreate,
                        modifier = Modifier.fillMaxWidth().height(44.dp),
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(tm.manualActivationButton, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(tm.directoryTitle, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) {
                        MembershipSearchField(query, onQueryChange)
                    }
                    if (canMembershipOverride) {
                        if (canWriteModule(AdminModuleAccess.MEMBERSHIPS)) Button(
                            onClick = onCreate,
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AdminColors.Primary,
                                contentColor = AdminColors.OnBrand,
                            ),
                        ) {
                            Text(tm.manualActivationButton, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay membresías todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    memberships.forEach { m ->
                        val client = clients.firstOrNull { it.id == m.clientId }
                        MembershipMobileCard(m, client, canMembershipOverride, onView, onEdit, onDelete, onHistory)
                    }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                MembershipsTable(
                    memberships,
                    clients,
                    listPaging = listPaging,
                    canMembershipOverride,
                    onView,
                    onEdit,
                    onDelete,
                    onHistory,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun MembershipSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

private fun formatMembershipVigencia(startsAt: String, endsAt: String?): String =
    if (endsAt.isNullOrBlank()) "$startsAt → —" else "$startsAt → $endsAt"

private fun membershipAccessLabel(item: AdminMembershipItem): String = when {
    item.accessMode == AdminMembershipAccessMode.MANUAL_BLOCKED -> "Sin acceso (bloqueada)"
    item.status == AdminMembershipStatus.ACTIVE -> "Puede entrar"
    item.status == AdminMembershipStatus.EXPIRED -> "Vencida"
    else -> "Cancelada"
}

private fun membershipRowAccent(item: AdminMembershipItem): Color = when {
    item.accessMode == AdminMembershipAccessMode.MANUAL_BLOCKED -> Color(0xFFC62828)
    item.status == AdminMembershipStatus.ACTIVE -> Color(0xFF2E7D32)
    item.status == AdminMembershipStatus.EXPIRED -> Color(0xFF757575)
    else -> Color(0xFFC62828)
}

@Composable
private fun MembershipsTable(
    memberships: List<AdminMembershipItem>,
    clients: List<AdminClient>,
    listPaging: AdminListPaging,
    canMembershipOverride: Boolean,
    onView: (AdminMembershipItem) -> Unit,
    onEdit: (AdminMembershipItem) -> Unit,
    onDelete: (AdminMembershipItem) -> Unit,
    onHistory: (AdminMembershipItem) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminSortableHeader("Cliente", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
            AdminSortableHeader("Plan", "plan", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Entrenador", "trainer", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Vigencia", "starts", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
            AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            memberships.forEach { m ->
                val client = clients.firstOrNull { it.id == m.clientId }
                MembershipAccessRow(
                    item = m,
                    client = client,
                    canMembershipOverride = canMembershipOverride,
                    onView = onView,
                    onEdit = onEdit,
                    onDelete = onDelete,
                    onHistory = onHistory,
                )
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun MembershipAccessRow(
    item: AdminMembershipItem,
    client: AdminClient?,
    canMembershipOverride: Boolean,
    onView: (AdminMembershipItem) -> Unit,
    onEdit: (AdminMembershipItem) -> Unit,
    onDelete: (AdminMembershipItem) -> Unit,
    onHistory: (AdminMembershipItem) -> Unit,
) {
    val accent = membershipRowAccent(item)
    val contact = listOfNotNull(client?.email, client?.docId?.let { "Céd. $it" }).joinToString(" · ")
    Row(
        Modifier
            .fillMaxWidth()
            .padding(vertical = 10.dp, horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .width(4.dp)
                .height(48.dp)
                .clip(RoundedCornerShape(4.dp))
                .background(accent),
        )
        Spacer(Modifier.width(10.dp))
        Column(Modifier.weight(1.2f)) {
            Text(item.clientName, fontWeight = FontWeight.Bold, fontSize = 14.sp, color = AdminColors.Text, maxLines = 1)
            if (contact.isNotBlank()) {
                Text(contact, color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1)
            }
        }
        Text(
            item.planName,
            Modifier.weight(1f),
            color = AdminColors.Text,
            fontSize = 13.sp,
            maxLines = 2,
        )
        Text(
            item.trainerName ?: "Sin entrenador",
            Modifier.weight(1f),
            color = AdminColors.Text,
            fontSize = 13.sp,
            maxLines = 2,
        )
        Column(Modifier.weight(1.1f)) {
            Text(
                formatMembershipVigencia(item.startsAt, item.endsAt),
                color = AdminColors.Text,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
            )
            item.endsAt?.let {
                Text("Vence $it", color = AdminColors.TextMuted, fontSize = 12.sp)
            }
        }
        Column(Modifier.weight(1f)) {
            MembershipStatusBadge(item.status)
            Spacer(Modifier.height(4.dp))
            Text(
                membershipAccessLabel(item),
                color = accent,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
            )
            if (item.accessMode != AdminMembershipAccessMode.AUTO) {
                Text(item.accessMode.labelEs(), color = AdminColors.TextMuted, fontSize = 10.sp)
            }
        }
        Row(Modifier.width(140.dp), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onHistory(item) }) {
                Icon(Icons.Outlined.History, "Historial", tint = AdminColors.Primary)
            }
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
            }
            if (canMembershipOverride) {
                if (canWriteModule(AdminModuleAccess.MEMBERSHIPS)) IconButton(onClick = { onEdit(item) }) {
                    Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.Primary)
                }
                if (canDeleteModule(AdminModuleAccess.MEMBERSHIPS)) IconButton(onClick = { onDelete(item) }) {
                    Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                }
            }
        }
    }
}

@Composable
private fun MembershipMobileCard(
    item: AdminMembershipItem,
    client: AdminClient?,
    canMembershipOverride: Boolean,
    onView: (AdminMembershipItem) -> Unit,
    onEdit: (AdminMembershipItem) -> Unit,
    onDelete: (AdminMembershipItem) -> Unit,
    onHistory: (AdminMembershipItem) -> Unit,
) {
    val accent = membershipRowAccent(item)
    val contact = listOfNotNull(client?.email, client?.docId?.let { "Céd. $it" }).joinToString(" · ")
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color(0xFFF8FBF8))
            .border(1.dp, accent.copy(alpha = 0.35f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Row(verticalAlignment = Alignment.Top) {
            Box(
                Modifier
                    .width(4.dp)
                    .height(52.dp)
                    .clip(RoundedCornerShape(4.dp))
                    .background(accent),
            )
            Spacer(Modifier.width(10.dp))
            Column(Modifier.weight(1f)) {
                Text(item.clientName, fontWeight = FontWeight.Bold, color = AdminColors.Text, fontSize = 15.sp)
                Text(item.planName, color = AdminColors.TextMuted, fontSize = 12.sp)
                if (contact.isNotBlank()) {
                    Text(contact, color = AdminColors.TextMuted, fontSize = 12.sp)
                }
            }
            MembershipStatusBadge(item.status)
        }
        Spacer(Modifier.height(10.dp))
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            MembershipInfoChip("Entrenador", item.trainerName ?: "Sin entrenador")
            MembershipInfoChip("Vigencia", formatMembershipVigencia(item.startsAt, item.endsAt))
        }
        Spacer(Modifier.height(8.dp))
        Text(
            membershipAccessLabel(item),
            color = accent,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
        )
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onHistory(item) }) {
                Icon(Icons.Outlined.History, null, tint = AdminColors.Primary)
            }
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            if (canMembershipOverride) {
                if (canWriteModule(AdminModuleAccess.MEMBERSHIPS)) IconButton(onClick = { onEdit(item) }) {
                    Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
                }
                if (canDeleteModule(AdminModuleAccess.MEMBERSHIPS)) IconButton(onClick = { onDelete(item) }) {
                    Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
                }
            }
        }
    }
}

@Composable
private fun MembershipInfoChip(label: String, value: String) {
    Column {
        Text(label, color = AdminColors.TextMuted, fontSize = 10.sp, fontWeight = FontWeight.Medium)
        Text(value, color = AdminColors.Text, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
    }
}

@Composable
private fun MembershipStatusBadge(status: AdminMembershipStatus) {
    Text(
        status.labelEs(),
        color = Color(status.badgeFgArgb()),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(999.dp))
            .background(Color(status.badgeBgArgb()))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun MembershipAccessHistoryDialog(
    item: AdminMembershipItem,
    clientMemberships: List<AdminMembershipItem>,
    onDismiss: () -> Unit,
) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(min = 640.dp, max = 920.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Outlined.History, null, tint = AdminColors.Primary, modifier = Modifier.size(22.dp))
                Text(
                    "Historial de membresías",
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.padding(start = 8.dp),
                )
            }
            Spacer(Modifier.height(6.dp))
            Text(
                item.clientName,
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
            Text(
                "Todas las membresías generadas para este socio.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
            Spacer(Modifier.height(14.dp))
            when {
                clientMemberships.isEmpty() -> Text(
                    "Sin membresías registradas para este socio.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                )
                else -> Column(
                    Modifier
                        .fillMaxWidth()
                        .heightIn(max = 440.dp)
                        .horizontalScroll(rememberScrollState()),
                ) {
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .padding(vertical = 8.dp, horizontal = 4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        AdminListHeaderLabel("Plan", Modifier.width(160.dp))
                        AdminListHeaderLabel("Entrenador", Modifier.width(140.dp))
                        AdminListHeaderLabel("Vigencia", Modifier.width(180.dp))
                        AdminListHeaderLabel("Estado", Modifier.width(100.dp))
                        AdminListHeaderLabel("Modo", Modifier.width(120.dp))
                        AdminListHeaderLabel("Alta", Modifier.width(110.dp))
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                    VerticalScrollColumn(
                        Modifier
                            .heightIn(max = 380.dp),
        ) {
                        clientMemberships.forEach { m ->
                            val highlight = m.id == item.id
                            Row(
                                Modifier
                                    .fillMaxWidth()
                                    .background(
                                        if (highlight) AdminColors.Primary.copy(alpha = 0.06f)
                                        else Color.Transparent,
                                    )
                                    .padding(vertical = 10.dp, horizontal = 4.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text(
                                    m.planName,
                                    Modifier.width(160.dp),
                                    color = AdminColors.Text,
                                    fontSize = 13.sp,
                                    fontWeight = if (highlight) FontWeight.SemiBold else FontWeight.Normal,
                                )
                                Text(
                                    m.trainerName ?: "—",
                                    Modifier.width(140.dp),
                                    color = AdminColors.Text,
                                    fontSize = 13.sp,
                                )
                                Text(
                                    formatMembershipVigencia(m.startsAt, m.endsAt),
                                    Modifier.width(180.dp),
                                    color = AdminColors.Text,
                                    fontSize = 13.sp,
                                )
                                Box(Modifier.width(100.dp)) {
                                    MembershipStatusBadge(m.status)
                                }
                                Text(
                                    m.accessMode.labelEs(),
                                    Modifier.width(120.dp),
                                    color = AdminColors.TextMuted,
                                    fontSize = 12.sp,
                                )
                                Text(
                                    formatHistoryTimestamp(m.createdAt.orEmpty()),
                                    Modifier.width(110.dp),
                                    color = AdminColors.TextMuted,
                                    fontSize = 12.sp,
                                )
                            }
                            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
                        }
                    }
                }
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss) {
                    Text("Cerrar", color = AdminColors.TextMuted)
                }
            }
        }
    }
}

private fun formatHistoryTimestamp(raw: String): String {
    if (raw.isBlank()) return "—"
    val instant = runCatching { Instant.parse(raw) }.getOrNull() ?: return raw.take(10)
    return instant.toString().take(10)
}

@Composable
private fun MembershipDetailDialog(
    item: AdminMembershipItem,
    canMembershipOverride: Boolean,
    onDismiss: () -> Unit,
    onBlocked: () -> Unit,
    onReleased: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    val pendingApprovals = LocalPendingApprovalNotifier.current
    var busy by remember { mutableStateOf(false) }
    var err by remember { mutableStateOf<String?>(null) }

    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 420.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text("Detalle de la membresía", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            DetailRow("Cliente", item.clientName)
            DetailRow("Plan", item.planName)
            DetailRow("Entrenador", item.trainerName ?: "—")
            DetailRow("Fecha de inicio", item.startsAt)
            DetailRow("Vigencia hasta", item.endsAt ?: "—")
            DetailRow("Estado", item.status.labelEs())
            DetailRow("Modo", item.accessMode.labelEs())
            item.manualReason?.let { DetailRow("Motivo manual", it) }
            item.manualByStaffName?.let { DetailRow("Gestionado por", it) }
            err?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }
            Spacer(Modifier.height(16.dp))
            if (canMembershipOverride) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (item.accessMode != AdminMembershipAccessMode.MANUAL_BLOCKED) {
                        OutlinedButton(
                            onClick = {
                                scope.launch {
                                    busy = true
                                    err = null
                                    try {
                                        AdminRepo.api.blockMembership(item.id, "Bloqueo manual desde detalle")
                                        onBlocked()
                                        onDismiss()
                                    } catch (e: Exception) {
                                        if (!pendingApprovals.offer(e)) {
                                            err = e.message
                                        } else {
                                            onDismiss()
                                        }
                                    } finally {
                                        busy = false
                                    }
                                }
                            },
                            enabled = !busy,
                        ) { Text("Bloquear") }
                    }
                    if (item.accessMode != AdminMembershipAccessMode.AUTO) {
                        OutlinedButton(
                            onClick = {
                                scope.launch {
                                    busy = true
                                    err = null
                                    try {
                                        AdminRepo.api.releaseMembershipAutomatic(item.id)
                                        onReleased()
                                        onDismiss()
                                    } catch (e: Exception) {
                                        if (!pendingApprovals.offer(e)) {
                                            err = e.message
                                        } else {
                                            onDismiss()
                                        }
                                    } finally {
                                        busy = false
                                    }
                                }
                            },
                            enabled = !busy,
                        ) { Text("Volver a automático") }
                    }
                }
                Spacer(Modifier.height(10.dp))
            }
            Button(
                onClick = onDismiss,
                modifier = Modifier.align(Alignment.End),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                ),
            ) {
                Text("Cerrar")
            }
        }
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun MembershipFormDialog(
    title: String,
    initial: AdminMembershipItem?,
    clients: List<AdminClient>,
    plans: List<AdminMembershipPlanItem>,
    companyId: String?,
    trainers: List<AdminTrainer>,
    saving: Boolean,
    manualOnly: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminMembershipUpsert) -> Unit,
) {
    var clientId by remember(initial) { mutableStateOf(initial?.clientId.orEmpty()) }
    var clientQuery by remember(initial) {
        mutableStateOf(initial?.clientName.orEmpty())
    }
    var planId by remember(initial) {
        mutableStateOf(initial?.planId ?: plans.firstOrNull()?.id.orEmpty())
    }
    val branchId = companyId
    var trainerId by remember(initial) { mutableStateOf(initial?.trainerId) }
    var status by remember(initial) { mutableStateOf(initial?.status ?: AdminMembershipStatus.ACTIVE) }
    var startsAt by remember(initial) { mutableStateOf(initial?.startsAt ?: todayIsoDate()) }
    var endsAt by remember(initial) { mutableStateOf(initial?.endsAt.orEmpty()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var clientMenu by remember { mutableStateOf(false) }
    var planMenu by remember { mutableStateOf(false) }
    var trainerMenu by remember { mutableStateOf(false) }
    var statusMenu by remember { mutableStateOf(false) }

    val filteredClients = remember(clients, clientQuery) {
        val q = clientQuery.trim().lowercase()
        if (q.isEmpty()) clients.take(12)
        else clients.filter {
            it.fullName.lowercase().contains(q) ||
                (it.email?.lowercase()?.contains(q) == true)
        }.take(12)
    }
    val selectedPlan = plans.firstOrNull { it.id == planId }
    val selectedTrainer = trainers.firstOrNull { it.id == trainerId }

    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(min = 640.dp, max = 720.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                if (manualOnly) {
                    Spacer(Modifier.height(6.dp))
                    Text(
                        "Activación manual. El cobro normal activa la membresía automáticamente.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
                Spacer(Modifier.height(14.dp))
                FormLabel("Cliente *")
                Box {
                    OutlinedTextField(
                        value = clientQuery,
                        onValueChange = {
                            clientQuery = it
                            clientMenu = true
                            if (clientId.isNotEmpty()) {
                                val match = clients.firstOrNull { c -> c.id == clientId }
                                if (match == null || match.fullName != it) clientId = ""
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        placeholder = { Text("Buscar clientes...", color = AdminColors.TextMuted) },
                        leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    DropdownMenu(expanded = clientMenu && filteredClients.isNotEmpty(), onDismissRequest = { clientMenu = false }) {
                        filteredClients.forEach { c ->
                            DropdownMenuItem(
                                text = { Text(c.fullName) },
                                onClick = {
                                    clientId = c.id
                                    clientQuery = c.fullName
                                    clientMenu = false
                                },
                            )
                        }
                    }
                }
                Spacer(Modifier.height(14.dp))
                // Celular (ancho < 500 dp): las columnas se apilan; en escritorio quedan lado a lado.
                BoxWithConstraints(Modifier.fillMaxWidth()) {
                val stacked = maxWidth < 500.dp
                val leftCol: @Composable () -> Unit = {
                        MembershipFormPlanTrainerFields(
                            selectedPlan = selectedPlan,
                            selectedTrainer = selectedTrainer,
                            onPlanMenu = { planMenu = true },
                            onTrainerMenu = { trainerMenu = true },
                        )
                }
                val rightCol: @Composable () -> Unit = {
                        MembershipFormDatesAndStatusFields(
                            startsAt = startsAt,
                            onStartsAt = { startsAt = it },
                            endsAt = endsAt,
                            onEndsAt = { endsAt = it },
                            manualOnly = manualOnly,
                            showStatus = initial != null,
                            status = status,
                            onStatusMenu = { statusMenu = true },
                        )
                }
                if (stacked) {
                    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                        leftCol()
                        rightCol()
                    }
                } else {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                        verticalAlignment = Alignment.Top,
                    ) {
                        Column(Modifier.weight(1f)) { leftCol() }
                        Column(Modifier.weight(1f)) { rightCol() }
                    }
                }
                }
                Box {
                    DropdownMenu(expanded = planMenu, onDismissRequest = { planMenu = false }) {
                        plans.forEach { p ->
                            DropdownMenuItem(
                                text = { Text(p.name) },
                                onClick = {
                                    planId = p.id
                                    planMenu = false
                                },
                            )
                        }
                    }
                    DropdownMenu(expanded = trainerMenu, onDismissRequest = { trainerMenu = false }) {
                        DropdownMenuItem(
                            text = { Text("Sin entrenador asignado") },
                            onClick = {
                                trainerId = null
                                trainerMenu = false
                            },
                        )
                        trainers.forEach { t ->
                            DropdownMenuItem(
                                text = { Text(t.displayName) },
                                onClick = {
                                    trainerId = t.id
                                    trainerMenu = false
                                },
                            )
                        }
                    }
                    if (initial != null) {
                        DropdownMenu(expanded = statusMenu, onDismissRequest = { statusMenu = false }) {
                            AdminMembershipStatus.entries.forEach { s ->
                                DropdownMenuItem(
                                    text = { Text(s.labelEs()) },
                                    onClick = {
                                        status = s
                                        statusMenu = false
                                    },
                                )
                            }
                        }
                    }
                }
                formError?.let {
                    Spacer(Modifier.height(8.dp))
                    Text(it, color = AdminColors.Error, fontSize = 12.sp)
                }
                Spacer(Modifier.height(18.dp))
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss, enabled = !saving) {
                        Text("Cancelar", color = AdminColors.TextMuted)
                    }
                    Spacer(Modifier.width(8.dp))
                    Button(
                        onClick = {
                            val startIso = parseFlexibleDateToIso(startsAt)
                            val endIso = endsAt.trim().takeIf { it.isNotBlank() }?.let { parseFlexibleDateToIso(it) }
                            when {
                                clientId.isBlank() -> formError = "Selecciona un cliente."
                                planId.isBlank() -> formError = "Selecciona un plan."
                                startIso == null ->
                                    formError = "Fecha de inicio inválida (DD/MM/AAAA o AAAA-MM-DD)."
                                endsAt.isNotBlank() && endIso == null ->
                                    formError = "Vigencia hasta inválida (DD/MM/AAAA o AAAA-MM-DD)."
                                else -> {
                                    formError = null
                                    onSave(
                                        AdminMembershipUpsert(
                                            clientId = clientId,
                                            planId = planId,
                                            status = status,
                                            startsAt = startIso,
                                            endsAt = endIso,
                                            branchId = branchId,
                                            trainerId = trainerId,
                                        ),
                                    )
                                }
                            }
                        },
                        enabled = !saving,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(if (saving) "…" else "Guardar")
                    }
                }
        }
    }
}

@Composable
private fun MembershipFormPlanTrainerFields(
    selectedPlan: AdminMembershipPlanItem?,
    selectedTrainer: AdminTrainer?,
    onPlanMenu: () -> Unit,
    onTrainerMenu: () -> Unit,
) {
    FormLabel("Plan")
    OutlinedButton(onClick = onPlanMenu, modifier = Modifier.fillMaxWidth()) {
        Text(selectedPlan?.name ?: "Seleccionar plan", color = AdminColors.Text)
    }
    Spacer(Modifier.height(10.dp))
    FormLabel("Entrenador")
    OutlinedButton(onClick = onTrainerMenu, modifier = Modifier.fillMaxWidth()) {
        Text(selectedTrainer?.displayName ?: "Sin entrenador asignado", color = AdminColors.Text)
    }
}

@Composable
private fun MembershipFormDatesAndStatusFields(
    startsAt: String,
    onStartsAt: (String) -> Unit,
    endsAt: String,
    onEndsAt: (String) -> Unit,
    manualOnly: Boolean,
    showStatus: Boolean,
    status: AdminMembershipStatus,
    onStatusMenu: () -> Unit,
) {
    FormLabel("Fecha de inicio")
    AdminDateField(
        value = startsAt,
        onValueChange = onStartsAt,
        placeholder = "AAAA-MM-DD o DD/MM/AAAA",
        outputFormat = AdminDateOutputFormat.ISO,
    )
    Spacer(Modifier.height(10.dp))
    FormLabel("Vigencia hasta (opcional)")
    AdminDateField(
        value = endsAt,
        onValueChange = onEndsAt,
        placeholder = "AAAA-MM-DD o DD/MM/AAAA",
        outputFormat = AdminDateOutputFormat.ISO,
    )
    if (showStatus) {
        Spacer(Modifier.height(10.dp))
        FormLabel("Estado")
        OutlinedButton(onClick = onStatusMenu, modifier = Modifier.fillMaxWidth()) {
            Text(status.labelEs(), color = AdminColors.Text)
        }
    }
}

@Composable
private fun FormLabel(text: String) {
    Text(text, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(bottom = 4.dp))
}

@Composable
private fun FormField(value: String, onChange: (String) -> Unit, placeholder: String) {
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

